import { ref, watch } from 'vue'

/**
 * 主题切换。
 *
 * 默认跟随系统，用户显式选择后才固定下来并记住——
 * 只有一次选择应当推翻系统偏好，而不是每次打开都强制某一种。
 */

const STORAGE_KEY = 'hc.theme'

export type ThemeMode = 'light' | 'dark' | 'system'

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function resolve(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return systemPrefersDark() ? 'dark' : 'light'
  }
  return mode
}

function readStored(): ThemeMode {
  const raw = localStorage.getItem(STORAGE_KEY)
  return raw === 'light' || raw === 'dark' ? raw : 'system'
}

const mode = ref<ThemeMode>(readStored())
const resolved = ref<'light' | 'dark'>(resolve(mode.value))

function apply(): void {
  resolved.value = resolve(mode.value)
  document.documentElement.dataset.theme = resolved.value
}

apply()

// 处于 system 模式时才跟随系统变化；用户已显式选择就不再被系统覆盖
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
  if (mode.value === 'system') {
    apply()
  }
})

watch(mode, (next) => {
  if (next === 'system') {
    localStorage.removeItem(STORAGE_KEY)
  } else {
    localStorage.setItem(STORAGE_KEY, next)
  }
  apply()
})

export function useTheme() {
  function toggle(): void {
    mode.value = resolved.value === 'dark' ? 'light' : 'dark'
  }

  return { mode, resolved, toggle }
}
